import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/auth/clerkShim';
import usePostStore from '@store/postStore';
import { orderPinnedFirst } from './editorStore';
import useLayoutStore from '@store/layoutStore';
import useOrganizationStore from '@store/organizationStore';
import useDivisionStore from '@store/divisionStore';
import useLeagueStore from '@store/leagueStore';
import helpers from '@/utils/helpers';
import { publicSiteUrl } from '@utils/publicSiteUrl';
import { newBlockId } from '@staty/shared/utils/blockUtils';
import useEditorStore, {
  Draft,
  LayoutDraft,
  LayoutEntity,
  PostDraft,
  PostEntity,
} from './editorStore';
import useAutosave from './useAutosave';
import CanvasTopBar from './CanvasTopBar';
import CanvasSidebar, { DivisionItem, SeasonGroup } from './CanvasSidebar';
import CanvasStage, { ActiveLayoutSurface } from './CanvasStage';
import SettingsPanel from './SettingsPanel';
import '@staty/shared/styles/blockContent.css';
import './canvas.css';

/**
 * The Squarespace-style canvas Site Editor (ADR 0033). The center IS the
 * page: pixel-accurate replicas of every authorable public surface — the
 * posts feed, custom nav pages, the org-home fallback page, and division
 * callouts — edited in place with typed blocks and real debounced autosave.
 * The real site is one click away in the top bar.
 */

interface OutletCtx {
  leagueName?: string;
}

// 'yyyy-mm-dd' from the date input → LOCAL midnight. `new Date('2026-08-12')`
// is UTC midnight, which renders as the previous day in any western timezone
// — the canvas card said Aug 12 while the public site said Aug 11.
function localMidnight(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export default function CanvasEditorAdmin() {
  const { getToken } = useAuth();
  const { leagueName = '' } = useOutletContext<OutletCtx>() || {};
  const [creating, setCreating] = useState(false);

  const storePosts = usePostStore((s: any) => s.posts) as PostEntity[];
  // The canvas mirrors /home: the pinned post first, regardless of date.
  const posts = useMemo(() => orderPinnedFirst(storePosts), [storePosts]);
  const fetchPosts = usePostStore((s: any) => s.fetchPosts(getToken));
  const addPost = usePostStore((s: any) => s.addPost(getToken));
  const updatePost = usePostStore((s: any) => s.updatePost(getToken));
  const savePostSilent = usePostStore((s: any) => s.savePostSilent(getToken));
  const deletePost = usePostStore((s: any) => s.deletePost(getToken));

  const layouts = useLayoutStore((s: any) => s.layouts) as LayoutEntity[];
  const fetchLayouts = useLayoutStore((s: any) => s.fetchLayouts);
  const addLayoutSilent = useLayoutStore((s: any) => s.addLayoutSilent(getToken));
  const saveLayoutSilent = useLayoutStore((s: any) => s.saveLayoutSilent(getToken));
  const deleteLayoutAction = useLayoutStore((s: any) => s.deleteLayout(getToken));

  const divisions = useDivisionStore((s: any) => s.divisions) as any[];
  const getDivisionsByLeagueName = useDivisionStore((s: any) => s.getDivisionsByLeagueName);
  const seasons = useLeagueStore((s: any) => s.leagues) as any[];
  const getSeasonsByLeagueName = useLeagueStore((s: any) => s.getLeaguesByOrganizationName);

  const organizations = useOrganizationStore((s: any) => s.organizations);
  const getOrganizationByName = useOrganizationStore((s: any) => s.getOrganizationByName);
  const organization = useMemo(
    () => (organizations || []).find((o: any) => o?.name === leagueName),
    [organizations, leagueName]
  );

  const surface = useEditorStore((s) => s.surface);
  const selected = useEditorStore((s) => s.selected);
  const selectPost = useEditorStore((s) => s.selectPost);
  const selectLayout = useEditorStore((s) => s.selectLayout);
  const forgetEntity = useEditorStore((s) => s.forgetEntity);
  const resetEditor = useEditorStore((s) => s.reset);

  const selectedPost =
    selected?.kind === 'post' ? posts.find((p) => p._id === selected.id) || null : null;

  useEffect(() => {
    fetchPosts(leagueName);
    fetchLayouts(leagueName);
    getOrganizationByName(leagueName);
    getDivisionsByLeagueName(leagueName);
    getSeasonsByLeagueName(leagueName);
    return () => resetEditor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueName]);

  /* ---- derived sidebar/stage data ---------------------------------------- */

  // Same lookups as the PUBLIC app, so the editor lists exactly what the
  // site resolves: nav = header layouts (OrganizationLayout), fallback home
  // = {name:'Home', pageLocation:'main'} with NO tier filter
  // (OrganizationPage), callouts = layout per division id (DivisionPage).
  const pages = useMemo(
    () =>
      layouts
        .filter((l) => l.pageLocation === 'header')
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [layouts]
  );
  const fallbackHome = useMemo(
    () => layouts.find((l) => l.name === 'Home' && l.pageLocation === 'main') || null,
    [layouts]
  );
  const layoutByDivision = useMemo(() => {
    const map = new Map<string, LayoutEntity>();
    for (const l of layouts) {
      if (l.division) map.set(String(helpers.getObjId(l.division)), l);
    }
    return map;
  }, [layouts]);

  const buildGroups = (contestType: string): SeasonGroup[] =>
    (seasons || [])
      .filter((s: any) => s.contestType === contestType)
      .map((season: any) => ({
        _id: String(season._id),
        name: season.name,
        divisions: (divisions || [])
          // `division.league` is the canonical link (CLAUDE.md hierarchy);
          // `season` is a legacy field on old documents. The retired editor
          // matched ONLY `season`, so its tree was silently empty for every
          // modern org.
          .filter(
            (d: any) =>
              String(helpers.getObjId(d.league ?? d.season ?? {})) === String(season._id)
          )
          .map((d: any) => ({
            _id: String(d._id),
            name: d.name,
            layout: layoutByDivision.get(String(d._id)) || null,
          })),
      }))
      .filter((g: SeasonGroup) => g.divisions.length > 0);

  const seasonGroups = useMemo(() => buildGroups('season'), [seasons, divisions, layoutByDivision]);
  const tournamentGroups = useMemo(() => buildGroups('tournament'), [seasons, divisions, layoutByDivision]);

  const layoutSurface: ActiveLayoutSurface | null = useMemo(() => {
    if (surface.kind !== 'layout') return null;
    const layout = layouts.find((l) => l._id === surface.id);
    if (!layout) return null;
    if (layout.division) {
      const div = (divisions || []).find(
        (d: any) => String(d._id) === String(helpers.getObjId(layout.division!))
      );
      return { layout, kind: 'division-callout', contextLabel: div?.name };
    }
    if (fallbackHome && layout._id === fallbackHome._id) {
      return { layout, kind: 'fallback-home' };
    }
    return { layout, kind: 'page' };
  }, [surface, layouts, divisions, fallbackHome]);

  /* ---- autosave ------------------------------------------------------------ */

  useAutosave(async (key: string, draft: Draft) => {
    if (draft.kind === 'post') {
      const d = draft as PostDraft;
      await savePostSilent({
        _id: key.slice('post:'.length),
        title: d.title,
        blocks: d.blocks,
        coverImage: d.coverImage,
        publishedAt: d.publishedAt ? localMidnight(d.publishedAt) : null,
        showTitle: d.showTitle,
        showDate: d.showDate,
      });
    } else {
      const d = draft as LayoutDraft;
      await saveLayoutSilent({
        _id: key.slice('layout:'.length),
        name: d.name,
        order: d.order,
        active: d.active,
        blocks: d.blocks,
      });
    }
  });

  /* ---- create / delete ----------------------------------------------------- */

  const handleNewPost = async () => {
    if (!organization?._id || creating) return;
    setCreating(true);
    await addPost(
      {
        organization: organization._id,
        title: '',
        status: 'draft',
        publishedAt: new Date(),
        blocks: [{ id: newBlockId(), type: 'richtext', html: '' }],
      },
      (created: PostEntity | undefined) => {
        if (created) selectPost(created);
      }
    );
    setCreating(false);
  };

  const handleNewPage = async () => {
    if (!organization?._id || creating) return;
    setCreating(true);
    try {
      // active:false — a new page enters the public nav only when the admin
      // flips "Show in navigation" (the schema default would hide it anyway;
      // being explicit documents the intent).
      const created = await addLayoutSilent({
        organization: organization._id,
        name: 'New Page',
        pageLocation: 'header',
        tier: 'organization',
        active: false,
        order: pages.length,
        blocks: [{ id: newBlockId(), type: 'richtext', html: '' }],
      });
      if (created) selectLayout(created);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not create page');
    }
    setCreating(false);
  };

  const handleSelectFallbackHome = async () => {
    if (fallbackHome) {
      selectLayout(fallbackHome);
      return;
    }
    // Orgs that predate the bootstrap have no Home layout — create it the
    // way the bootstrap does (active so the fallback actually renders).
    if (!organization?._id || creating) return;
    setCreating(true);
    try {
      const created = await addLayoutSilent({
        organization: organization._id,
        name: 'Home',
        pageLocation: 'main',
        tier: 'organization',
        active: true,
        blocks: [{ id: newBlockId(), type: 'richtext', html: '' }],
      });
      if (created) selectLayout(created);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not create the fallback page');
    }
    setCreating(false);
  };

  const handleSelectDivision = async (division: DivisionItem) => {
    if (division.layout) {
      selectLayout(division.layout);
      return;
    }
    if (!organization?._id || creating) return;
    setCreating(true);
    try {
      // Created empty: the public callout hides itself while the content is
      // visually empty (blocksAreEmpty), so this write is invisible until
      // the admin actually types something.
      const created = await addLayoutSilent({
        organization: organization._id,
        division: division._id,
        name: division.name,
        pageLocation: 'main',
        tier: 'division',
        active: true,
        blocks: [{ id: newBlockId(), type: 'richtext', html: '' }],
      });
      if (created) selectLayout(created);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not create the callout');
    }
    setCreating(false);
  };

  // Pinned post (INT-0048): an explicit click, like publish. The store clears
  // the previous pin locally from the saved post's response.
  const handlePinToggle = (post: PostEntity, pinned: boolean) => {
    updatePost({ _id: post._id, pinned });
  };

  const handlePublishToggle = (post: PostEntity, publish: boolean) => {
    // Explicit action → the toasting updatePost is right here (feedback for
    // a deliberate click, unlike autosave).
    updatePost({ _id: post._id, status: publish ? 'published' : 'draft' });
  };

  const handleDeletePost = (post: PostEntity) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${post.title || 'Untitled post'}"? This removes it from your site.`)) return;
    deletePost(post, () => forgetEntity({ kind: 'post', id: post._id }));
  };

  const handleDeleteLayout = (layoutId: string) => {
    const layout = layouts.find((l) => l._id === layoutId);
    const label = layoutSurface?.kind === 'division-callout' ? 'callout' : 'page';
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete this ${label}${layout?.name ? ` ("${layout.name}")` : ''}? This removes it from your site.`)) return;
    deleteLayoutAction({ _id: layoutId }, () => forgetEntity({ kind: 'layout', id: layoutId }));
  };

  return (
    <div className="canvas-editor">
      {/* The AdminRail + org-switcher header come from OrganizationLayout
          (rail mode) since the /admin/editor move (owner 2026-09-11) — the
          editor only renders its own top bar and three-zone body. */}
      <CanvasTopBar leagueName={leagueName} previewUrl={publicSiteUrl(leagueName, '/home')} />
      <div className="canvas-editor__body">
        <CanvasSidebar
          posts={posts}
          pages={pages}
          fallbackHome={fallbackHome}
          seasonGroups={seasonGroups}
          tournamentGroups={tournamentGroups}
          creating={creating}
          onSelectPost={selectPost}
          onNewPost={handleNewPost}
          onSelectLayout={selectLayout}
          onNewPage={handleNewPage}
          onSelectFallbackHome={handleSelectFallbackHome}
          onSelectDivision={handleSelectDivision}
        />
        <CanvasStage
          posts={posts}
          leagueName={leagueName}
          layoutSurface={layoutSurface}
          onSelectPost={selectPost}
        />
        <SettingsPanel
          post={selectedPost}
          layoutSurface={layoutSurface}
          onPublishToggle={handlePublishToggle}
          onPinToggle={handlePinToggle}
          onDelete={handleDeletePost}
          onDeleteLayout={handleDeleteLayout}
        />
      </div>
    </div>
  );
}
